export const API_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export function getCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

let csrfPrimed = false;
async function primeCsrf(): Promise<void> {
  if (csrfPrimed || getCookie('csrftoken')) {
    csrfPrimed = true;
    return;
  }
  await fetch(`${API_URL}/accounts/csrf/`, { credentials: 'include' });
  csrfPrimed = true;
}

type HeaderMap = Record<string, string>;

async function jsonHeaders(extra: HeaderMap = {}): Promise<HeaderMap> {
  await primeCsrf();
  const token = getCookie('csrftoken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'X-CSRFToken': token } : {}),
    ...extra,
  };
}

async function unsafeHeaders(extra: HeaderMap = {}): Promise<HeaderMap> {
  await primeCsrf();
  const token = getCookie('csrftoken');
  return { ...(token ? { 'X-CSRFToken': token } : {}), ...extra };
}

/**
 * Error shape thrown by apiClient when the response is non-2xx. The
 * server's JSON body (if any) is exposed on `.body` so callers can pick
 * out structured validation errors.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  let body: unknown = null;
  try { body = await response.json(); } catch { body = null; }
  if (!response.ok) {
    let message: string = response.statusText;
    if (body && typeof body === 'object') {
      const b = body as { error?: unknown; detail?: unknown };
      if (typeof b.error === 'string' && b.error) message = b.error;
      else if (typeof b.detail === 'string' && b.detail) message = b.detail;
    }
    throw new ApiError(message, response.status, body);
  }
  return body as T;
}

export interface ApiClient {
  get: <T = unknown>(endpoint: string) => Promise<T>;
  post: <T = unknown>(endpoint: string, data: unknown) => Promise<T>;
  put: <T = unknown>(endpoint: string, data: unknown) => Promise<T>;
  patch: <T = unknown>(endpoint: string, data: unknown) => Promise<T>;
  delete: (endpoint: string) => Promise<boolean>;
  uploadFile: <T = unknown>(endpoint: string, formData: FormData) => Promise<T>;
}

export const apiClient: ApiClient = {
  get: async <T = unknown>(endpoint: string) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    return parseJsonOrThrow<T>(response);
  },

  post: async <T = unknown>(endpoint: string, data: unknown) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: await jsonHeaders(),
      body: JSON.stringify(data),
    });
    return parseJsonOrThrow<T>(response);
  },

  put: async <T = unknown>(endpoint: string, data: unknown) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      credentials: 'include',
      headers: await jsonHeaders(),
      body: JSON.stringify(data),
    });
    return parseJsonOrThrow<T>(response);
  },

  patch: async <T = unknown>(endpoint: string, data: unknown) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: await jsonHeaders(),
      body: JSON.stringify(data),
    });
    return parseJsonOrThrow<T>(response);
  },

  delete: async (endpoint: string) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await unsafeHeaders(),
    });
    return response.ok;
  },

  uploadFile: async <T = unknown>(endpoint: string, formData: FormData) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: await unsafeHeaders(),
      body: formData,
    });
    return parseJsonOrThrow<T>(response);
  },
};

export default apiClient;
